import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { inventoryList as inventoryListService } from '@services/inventoryList';

import { exitModal } from '@commons/utils/exportModalWithProgress';

import ExportModalContent from '@lib/inpulse/ExportModal';

import { ErrorContainer, AlreadyProccessedIds } from './styledComponents';
import Text from '@commons/Text';

const BATCH_TO_PROCESS_AT_ONCE = 50;

/**
 * Modal to handle adding references with zero progressively by batch.
 *
 * @param {Object} props
 * @param {Object} props.params
 * @param {string} props.params.title - Modal title
 * @param {Array<string>} props.params.inventoryListIds - List of inventory list IDs to process
 * @param {Array<string>} props.params.supplierProductIds - List of supplier product IDs
 * @param {function} props.params.onSuccess - Function called on success
 */
const AddReferencesWithZeroProgressModal = (props) => {
  const {
    params: { title, inventoryListIds, supplierProductIds, onSuccess },
    closeModal,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [titleModal, setTitle] = useState(
    title || i18next.t('STOCKS.STOCKS.LIST_ADD_REFERENCES_WITH_ZERO'),
  );
  const [inventoryIdsToProcess, setInventoryIds] = useState([...inventoryListIds]);
  const [processedCount, setProcessedCount] = useState(0);
  const [alreadyProcessedInventoryLists, setAlreadyProcessedInventoryLists] = useState(null);

  useEffect(() => {
    processByBatchOfIds([...inventoryIdsToProcess]);
  }, [inventoryIdsToProcess]);

  const processByBatchOfIds = async (inventoryIds) => {
    if (!inventoryIds.length && !processedCount) {
      return;
    }

    // If no more ids, then all data has been processed
    if (!inventoryIds.length) {
      setProgress(100);
      setTitle(i18next.t('STOCKS.STOCKS.LIST_ADD_REFERENCES_WITH_ZERO_SUCCESS'));
      setLoading(false);

      if (onSuccess) {
        onSuccess();
      }
      return;
    }

    const batchIds = inventoryIds.splice(0, BATCH_TO_PROCESS_AT_ONCE);

    try {
      await inventoryListService.resetStockWithZero(batchIds, supplierProductIds);

      const newProcessedCount = processedCount + batchIds.length;
      setProcessedCount(newProcessedCount);

      updateProgress(inventoryIds, newProcessedCount);

      setInventoryIds(inventoryIds);
    } catch (error) {
      // Extract already processed inventory list IDs from error
      const processedInventoryLists =
        error?.response?.data?.alreadyProcessedInventoryListReferences;
      setAlreadyProcessedInventoryLists(processedInventoryLists);

      // Handle error
      setTitle(i18next.t('STOCKS.STOCKS.LIST_ADD_REFERENCES_WITH_ZERO_ERROR'));
      setLoading(false);
    }
  };

  const updateProgress = (inventoryIds, newProcessedCount) => {
    const remainingInventories = inventoryIds.length;
    const totalInventories = remainingInventories + newProcessedCount;
    const updatedProgress = 100 - (remainingInventories / totalInventories) * 100;
    setProgress(updatedProgress);
  };

  // Render error details when there are already processed IDs
  const renderErrorDetails = () => {
    if (!alreadyProcessedInventoryLists || alreadyProcessedInventoryLists.length === 0) {
      return null;
    }

    return (
      <ErrorContainer>
        <Text>{i18next.t('STOCKS.STOCKS.LIST_ADD_REFERENCES_WITH_ZERO_ERROR_DETAILS')}</Text>
        <AlreadyProccessedIds>
          {alreadyProcessedInventoryLists.map((reference) => (
            <Text key={reference}>{reference}</Text>
          ))}
        </AlreadyProccessedIds>
      </ErrorContainer>
    );
  };

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setLoading}
      titleModal={titleModal}
    >
      {renderErrorDetails()}
    </ExportModalContent>
  );
};

export default AddReferencesWithZeroProgressModal;
